import { useToast } from 'primevue/usetoast';
import { useChatStore } from '@/store/chatStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useUserStore } from '@/store/userStore';
import { ChatChannelType } from '@/types/composition-functions';
import {
  useChatUnreadSummaryQuery,
  useChatMessageSentSubscription,
} from '@/types/vue-apollo-helpers';
import { showLocalNotification } from '@/services/pushNotification';

// Shape of ChatMessageType.metadata (a free-form JSON scalar on the wire).
// @allow-handwritten narrowing of an untyped JSON scalar; not a schema type
interface ChatMentionsMetadata {
  mentions?: {
    user_ids?: Array<number | string>;
    everyone?: boolean;
  };
}

/**
 * Shell-level chat attention: keeps the sidebar unread badge fed from
 * chatUnreadSummary and, via the General-channel subscription, surfaces
 * messages that arrive while the user is anywhere else in the app — as a
 * toast, a bell entry, and (when the tab is hidden) a browser notification.
 * Mount once per dashboard shell.
 */
export function useChatNotifications() {
  const chatStore = useChatStore();
  const notificationStore = useNotificationStore();
  const userStore = useUserStore();
  const toast = useToast();
  const route = useRoute();

  const currentUserId = computed(() => String(userStore.currentUser?.id ?? ''));

  const { result: summaryResult, refetch: refetchSummary } = useChatUnreadSummaryQuery({
    pollInterval: 60000,
    fetchPolicy: 'network-only',
  });

  watch(summaryResult, (res) => {
    const summary = res?.chatUnreadSummary;
    if (!summary) return;
    chatStore.setTotalUnread(summary.total_unread);
    const general = summary.channels.find((c) => c.channel_type === ChatChannelType.SchoolGeneral);
    chatStore.setGeneralChannelId(general ? String(general.channel_id) : null);
  });

  // SchoolChat bumps this after markChannelRead so the badge clears at once.
  watch(() => chatStore.refreshTick, () => {
    refetchSummary();
  });

  const { result: messageResult } = useChatMessageSentSubscription(
    () => ({ chat_channel_id: chatStore.generalChannelId ?? '' }),
    () => ({ enabled: !!chatStore.generalChannelId }),
  );

  watch(messageResult, (res) => {
    const msg = res?.chatMessageSent;
    if (!msg) return;
    if (String(msg.sender_id) === currentUserId.value) return;
    // On a chat page the component already renders the message and marks it read.
    if (route.name === 'admin-chat' || route.name === 'parent-messages') return;

    chatStore.incrementUnread();

    const meta = (msg.metadata ?? null) as ChatMentionsMetadata | null;
    const mentions = meta?.mentions;
    const mentioned = !!mentions && (
      mentions.everyone === true
      || (mentions.user_ids ?? []).some((id) => String(id) === currentUserId.value)
    );

    const preview = msg.body.length > 120 ? `${msg.body.slice(0, 120)}…` : msg.body;
    const title = mentioned
      ? `${msg.sender_name} mentioned you in General`
      : 'New message in General';
    const message = `${msg.sender_name}: ${preview}`;

    toast.add({
      severity: mentioned ? 'warn' : 'info',
      summary: title,
      detail: mentioned ? preview : message,
      life: 6000,
    });

    notificationStore.addNotification({
      id: `chat-${msg.id}`,
      title,
      message,
      type: 'chat_message',
      read: false,
      created_at: String(msg.created_at),
      metadata: { channel_id: msg.chat_channel_id },
    });

    if (document.hidden) {
      showLocalNotification(title, message, {
        type: 'chat_message',
        channel_id: msg.chat_channel_id,
      });
    }
  });

  return {
    totalUnread: computed(() => chatStore.totalUnread),
  };
}
